import { useEffect, useRef } from 'react';

import { AD_CLIENT_ID } from '../config/public-config.js';
import { useStrings } from '../i18n.js';

/**
 * Display-ad inventory (Google AdSense).
 *
 * Four rules, enforced structurally rather than by convention:
 *
 *   1. **Never on a private route.** This component is imported only by public content
 *      pages. The private room is a separate build entry with no import path to it, the
 *      AdSense script is injected only into prerendered public HTML, and the room's
 *      Content-Security-Policy permits no external origin at all. Three independent
 *      barriers, each of which alone would stop an ad appearing next to somebody's
 *      private amount.
 *   2. **Explicit units, placed after the content.** Each unit here sits below the prose
 *      it follows and is labelled. Auto Ads are enabled for this site as well, after the
 *      placements were reviewed in the AdSense dashboard; they can only appear on public
 *      pages, because the ad script is loaded on public pages alone. Whatever the source,
 *      no ad may sit beside the Create or Submit action; see DEPLOYMENT.md.
 *   3. **No inline script.** The `adsbygoogle.push` call happens here, in the bundle, so
 *      the public CSP never needs `'unsafe-inline'` for scripts.
 *   4. **Nothing fake.** With no ad client configured, production renders nothing at all;
 *      development renders a labelled outline so the layout can be reviewed.
 *
 * Consent: a TCF v2.3 CMP is required before serving EEA/UK/Swiss traffic, and it loads
 * on public pages only. See DEPLOYMENT.md §10.
 */

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

interface AdSlotProps {
  /** Which unit this is, for reporting in the AdSense dashboard. */
  slot: string;
  /** The AdSense ad unit id (`data-ad-slot`). Falls back to a responsive auto unit. */
  adUnitId?: string;
}

export function AdSlot({ slot, adUnitId }: AdSlotProps) {
  const s = useStrings();
  const enabled = AD_CLIENT_ID !== '';
  const pushed = useRef(false);

  useEffect(() => {
    if (!enabled || pushed.current) return;
    // Guard against React 19 StrictMode double-invoking effects, which would otherwise
    // ask AdSense to fill the same slot twice and log a TagError.
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      // An ad blocker, a failed script load, or no fill. None of that is worth surfacing
      // to the reader, and the reserved space simply stays empty.
    }
  }, [enabled]);

  if (!enabled) {
    if (!import.meta.env.DEV) return null;
    return (
      <aside className="ad-slot" aria-hidden="true">
        <div className="ad-slot__frame">{s.ads.inactive(slot)}</div>
      </aside>
    );
  }

  return (
    <aside className="ad-slot" aria-label={s.ads.label}>
      <p className="ad-slot__label">{s.ads.label}</p>
      <ins
        className="adsbygoogle ad-slot__unit"
        data-ad-client={AD_CLIENT_ID}
        {...(adUnitId ? { 'data-ad-slot': adUnitId } : {})}
        data-ad-format="auto"
        data-full-width-responsive="true"
        data-ad-region={slot}
      />
    </aside>
  );
}
